import React, { useContext, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useTenant } from "../../../../Context/Tenant/TenantContext";
import { ADD_TENANT_APP, SET_NOTIFICATION_MESSAGE, SET_NOTIFICATION_TYPE, SET_SELECTED_TENANT_ID, TOGGLE_SHOW_ADD_APP_DIALOG, TOGGLE_SHOW_NOTIFICATION, UPDATE_TENANT } from "../TenantEvents";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { addTenantProject } from "../../../../API/Tenant/Tenant";
import InnerLoader from "../../../Shared/Loader/InnerLoader";
import { OverlayLoader } from "../../../Shared/Loader/OverlayLoader";
import { sendEmail } from "../../../../API/EmailApi";
import { GlobalContext } from "../../../../Renderers/GlobalContext";
import { useTranslation } from "react-i18next";

const AddTenantAppDialog = () => {
    const { state, dispatch } = useTenant();
    const { user, subscription } = useContext(GlobalContext).state;
    const [inProgress, setInProgress] = useState(false);

    const selectedTenant = state.tenants.find(tenant => tenant._id === state.selectedTenantId);
    const { masterProjects } = selectedTenant || { masterProjects: [] };

    const activeMasterProjects = state.projects.filter(project => project.isActive && project.masterApp);
    const filteredProjects = activeMasterProjects.filter(project => !masterProjects.some(masterProject => masterProject.masterProjectId === project._id));
    const [selectedProjectId, setSelectedProjectId] = useState(filteredProjects[0]?._id || null);
    const [selectedInstanceId, setSelectedInstanceId] = useState(selectedProjectId ? filteredProjects[0].instances[0]?._id : null);

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const handleAddApp = async () => {
        if (!selectedProjectId) return;

        setInProgress(true);
        try {
            const response = await addTenantProject(selectedTenant._id, {
                masterProjectId: selectedProjectId,
                masterAppId: selectedInstanceId,
                subscriptionId: subscription._id,
            });
            if (response.masterProjectId) {
                const firstName = user.firstName || user.email.split('@')[0];
                const lastName = user.lastName || "";
                const invitedUserFirstName = selectedTenant.tenantUser.firstName || selectedTenant.tenantEmail.split('@')[0];
                const designerUrl = selectedTenant.tenantOrganization.orgName ? `${process.env.REACT_APP_DESIGNER_URL}` : process.env.REACT_APP_DESIGNER_URL + "/signup";

                const templateProperties = {
                    subject: "Invitation to Add a New App to Your Organization",
                    invitedUserFirstName,
                    firstName,
                    lastName,
                    designerUrl,
                }
                await sendEmail(selectedTenant.tenantEmail, process.env.REACT_APP_SENDGGRID_INVITE_TENANT_OWNER_TO_APP_KEY, templateProperties);
                dispatch({
                    type: ADD_TENANT_APP,
                    payload: {
                        id: selectedTenant._id,
                        data: response
                    }
                });
                dispatch({
                    type: UPDATE_TENANT,
                    payload: {
                        id: selectedTenant._id,
                        data: {
                            expanded: true
                        }
                    }
                });
            } else {
                dispatch({ type: SET_NOTIFICATION_MESSAGE, payload: response.message });
                dispatch({ type: TOGGLE_SHOW_NOTIFICATION });
                dispatch({ type: SET_NOTIFICATION_TYPE, payload: "error" });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setInProgress(false);
            handleClose();
        }
    };

    const handleClose = () => {
        dispatch({ type: SET_SELECTED_TENANT_ID, payload: null });
        dispatch({ type: TOGGLE_SHOW_ADD_APP_DIALOG });
    };

    const handleSelectedProjectChange = (e) => {
        const project = filteredProjects.find(project => project._id === e.target.value._id);
        setSelectedProjectId(e.target.value._id);
        setSelectedInstanceId(project?.instances[0]?._id || null);
    };

    const handleSelectedInstanceChange = (e) => {
        setSelectedInstanceId(e.target.value._id);
    }

    const getProjectInstances = (projectId) => {
        const project = filteredProjects.find(project => project._id === projectId);
        if (project) {
            return project.instances.filter(instance => instance.Published && instance.isActive);
        }
        return [];
    }

    return (
        <Dialog
            title={t("new_app_invitation")}
            onClose={handleClose}
            width={450}
            height={300}
        >
            <form>
                {inProgress && <OverlayLoader />}
                <div className="mb-3">
                    <label className="form-label">
                        {t("choose_app")}
                    </label>
                    <DropDownList
                        data={filteredProjects}
                        textField="name"
                        dataItemKey="_id"
                        value={filteredProjects.find(project => project._id === selectedProjectId)}
                        onChange={handleSelectedProjectChange}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label">
                        {t("choose_environment")}
                    </label>
                    <DropDownList
                        data={getProjectInstances(selectedProjectId)}
                        textField="instanceName"
                        dataItemKey="_id"
                        value={filteredProjects.find(project => project._id === selectedProjectId)?.instances.find(instance => instance._id === selectedInstanceId)}
                        onChange={handleSelectedInstanceChange}
                        disabled={!selectedProjectId}
                    />
                </div>
            </form>
            <DialogActionsBar layout="end">
                {inProgress && <InnerLoader borderColor="green" />}
                <div className="mb-2 me-3">
                    <button className="btn btn-secondary" onClick={handleClose}>
                        {t("cancel_button")}
                    </button>
                    <button
                        disabled={!selectedProjectId || !selectedInstanceId || inProgress}
                        onClick={handleAddApp}
                        className="btn btn-primary"
                    >
                        {t("send_invite_button")}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default AddTenantAppDialog;
